import * as Sentry from '@sentry/nuxt'

const CHUNK_RELOAD_KEY = '__hc_chunk_reloads__'
const MAX_RELOADS = 3
const RELOAD_RESET_MS = 30_000

/** Collapse simultaneous app:chunkError + unhandledrejection + async onError into one reload. */
let reloadScheduled = false
let recoveryFailureReported = false

export function isChunkLoadError(error: unknown): boolean {
    const message = String((error as { message?: string })?.message || error || '').toLowerCase()

    return message.includes("couldn't resolve component")
        || message.includes("reading 'default'")
        || message.includes("evaluating 'e.default'")
        || message.includes('importing a module script failed')
        || message.includes('failed to fetch dynamically imported module')
        || message.includes('loading chunk')
        || message.includes('chunk')
        || message.includes('dynamically imported')
        || message.includes('module script')
        || message.includes('.css')
}

export function isKeepAliveTeardownError(error: unknown): boolean {
    const message = String((error as { message?: string })?.message || error || '')

    return message.includes("reading 'da'")
}

function toError(error: unknown): Error {
    if (error instanceof Error) {
        return error
    }

    return new Error(String(error ?? 'Chunk load failed'))
}

function reportChunkRecoveryFailure(error: unknown): void {
    if (recoveryFailureReported || !import.meta.client) {
        return
    }

    recoveryFailureReported = true

    const err = toError(error)
    const message = err.message
    const chunkUrlMatch = message.match(/https?:\/\/[^\s"']+\/_nuxt\/[^\s"']+/)

    Sentry.captureException(err, {
        level: 'error',
        tags: {
            chunk_load_failed: 'true',
            chunk_recovery_failed: 'true',
            chunk_reload_count: String(MAX_RELOADS),
            ...(chunkUrlMatch?.[0] ? { chunk_url: chunkUrlMatch[0] } : {}),
        },
        contexts: {
            chunk_recovery: {
                path: window.location.pathname,
                maxReloads: MAX_RELOADS,
                reloadResetMs: RELOAD_RESET_MS,
            },
        },
    })
}

/**
 * Full page reload after deploy/chunk miss. Returns whether a reload was scheduled.
 * Sentry is notified only when reloads are exhausted (recovery failed).
 */
export function triggerChunkReloadOnce(error?: unknown): boolean {
    if (!import.meta.client || reloadScheduled) {
        return false
    }

    const now = Date.now()
    let stored = { count: 0, ts: 0 }

    try {
        stored = JSON.parse(sessionStorage.getItem(CHUNK_RELOAD_KEY) || '{"count":0,"ts":0}')
    } catch {}

    if (now - stored.ts > RELOAD_RESET_MS) {
        stored.count = 0
    }

    if (stored.count >= MAX_RELOADS) {
        reportChunkRecoveryFailure(error)
        return false
    }

    reloadScheduled = true

    const newCount = stored.count + 1
    sessionStorage.setItem(CHUNK_RELOAD_KEY, JSON.stringify({ count: newCount, ts: now }))
    try {
        sessionStorage.setItem('__hc_chunk_reload_meta__', JSON.stringify({
            ts: now,
            path: window.location.pathname,
            count: newCount,
        }))
    } catch {}

    // `location.href = location.href` is often a no-op (same URL / hash) — use reload().
    window.location.reload()
    return true
}

function targetLooksLikeNuxtAsset(target: EventTarget | null): boolean {
    const el = target as HTMLScriptElement | HTMLLinkElement | null
    if (!el) return false

    const url = (el as HTMLScriptElement).src || (el as HTMLLinkElement).href || ''

    return typeof url === 'string' && (url.includes('/_nuxt/') || url.includes('/_payload'))
}

/**
 * Catch chunk/css load failures that do not pass through Nuxt hooks.
 */
export function installChunkErrorWatchers(): void {
    if (!import.meta.client) return

    window.addEventListener(
        'error',
        (event: Event) => {
            const errEvent = event as ErrorEvent
            const payload = errEvent.error ?? errEvent.message
            if (
                isChunkLoadError(payload)
                || isKeepAliveTeardownError(payload)
                || targetLooksLikeNuxtAsset(event.target)
            ) {
                const assetUrl = (event.target as HTMLScriptElement | HTMLLinkElement | null)?.src
                    || (event.target as HTMLLinkElement | null)?.href
                triggerChunkReloadOnce(
                    payload
                    || (assetUrl ? new Error(`Failed to load Nuxt asset: ${assetUrl}`) : undefined),
                )
            }
        },
        true,
    )

    window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
        if (isChunkLoadError(event.reason) || isKeepAliveTeardownError(event.reason)) {
            triggerChunkReloadOnce(event.reason)
        }
    })
}
